import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Mail, CheckCircle, XCircle, RefreshCw, Loader2, 
  Send, Clock, AlertTriangle, Search, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import AdminSidebar from '../../components/admin/AdminSidebar';

const API_URL = window.location.origin;

export default function AdminEmailLogsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ sent: 0, failed: 0, retrying: 0 });
  const [failedEmails, setFailedEmails] = useState([]);
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  
  const [triggerDialog, setTriggerDialog] = useState(false);
  const [triggerBookingRef, setTriggerBookingRef] = useState('');
  const [triggerEmailType, setTriggerEmailType] = useState('confirmation');
  const [triggering, setTriggering] = useState(false);
  
  const token = localStorage.getItem('admin_token') || localStorage.getItem('vipgez_admin_token') || localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/login-goturkeytransfer');
    }
  }, [token, navigate]);

  const fetchLogs = useCallback(async () => {
    try {
      let url = `${API_URL}/api/admin/emails/logs?page=${page}&limit=30`;
      if (statusFilter && statusFilter !== 'all') url += `&status=${statusFilter}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 401) {
        navigate('/login-goturkeytransfer');
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        setLogs(data.logs);
        setTotalPages(data.totalPages);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  }, [token, navigate, page, statusFilter, search]);

  const fetchFailedEmails = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/emails/failed?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (data.success) {
        setFailedEmails(data.failed_emails);
      }
    } catch (error) {
      console.error('Error fetching failed emails:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLogs(), fetchFailedEmails()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLogs, fetchFailedEmails]);

  const handleTriggerEmail = async () => {
    if (!triggerBookingRef.trim()) {
      toast.error('Please enter a booking reference');
      return;
    }
    
    setTriggering(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/emails/trigger/${triggerBookingRef}/${triggerEmailType}`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        toast.success(data.message || 'Email triggered successfully');
        setTriggerDialog(false);
        setTriggerBookingRef('');
        fetchLogs();
      } else {
        toast.error(data.detail || 'Failed to trigger email');
      }
    } catch (error) {
      toast.error('Failed to trigger email');
    } finally {
      setTriggering(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <CheckCircle className="h-3 w-3" /> Sent
          </span>
        );
      case 'failed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
            <XCircle className="h-3 w-3" /> Failed
          </span>
        );
      case 'pending_retry':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
            <Clock className="h-3 w-3" /> Retrying
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('tr-TR');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex admin-theme">
        <AdminSidebar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex admin-theme">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Email Management</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Monitor email delivery and manage retries
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { fetchLogs(); fetchFailedEmails(); }}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button 
            onClick={() => setTriggerDialog(true)}
            className="gap-2 bg-orange-500 hover:bg-orange-600"
            data-testid="trigger-email-btn"
          >
            <Send className="h-4 w-4" />
            Send Email
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/10 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.sent}</p>
                <p className="text-sm text-muted-foreground">Emails Sent</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500/10 rounded-lg">
                <XCircle className="h-6 w-6 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.failed}</p>
                <p className="text-sm text-muted-foreground">Failed</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-secondary border-border">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/10 rounded-lg">
                <Clock className="h-6 w-6 text-yellow-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{stats.retrying}</p>
                <p className="text-sm text-muted-foreground">Pending Retry</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Failed Emails Alert */}
      {failedEmails.length > 0 && (
        <Card className="border-yellow-500/30 bg-yellow-500/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-yellow-500">
              <AlertTriangle className="h-4 w-4" />
              Emails Pending Retry ({failedEmails.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {failedEmails.slice(0, 5).map((email, idx) => (
                <div key={idx} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="font-medium text-foreground">{email.booking_ref}</span>
                    <span className="text-muted-foreground ml-2">({email.email_type})</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Retry {email.retry_count}/3 • Next: {formatDate(email.next_retry_at)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email or booking ref..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchLogs()}
            className="pl-10"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchLogs}>
          <Filter className="h-4 w-4" />
        </Button>
      </div>

      {/* Logs Table */}
      <Card className="bg-secondary border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-4 font-medium text-foreground">To</th>
                  <th className="text-left p-4 font-medium text-foreground">Subject</th>
                  <th className="text-left p-4 font-medium text-foreground">Status</th>
                  <th className="text-left p-4 font-medium text-foreground">Sent At</th>
                  <th className="text-left p-4 font-medium text-foreground">Error</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center p-8 text-muted-foreground">
                      No email logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log, idx) => (
                    <tr key={idx} className="border-b border-border hover:bg-muted/30">
                      <td className="p-4">
                        <span className="font-medium text-foreground">{log.to_email}</span>
                      </td>
                      <td className="p-4 text-muted-foreground max-w-xs truncate">
                        {log.subject}
                      </td>
                      <td className="p-4">{getStatusBadge(log.status)}</td>
                      <td className="p-4 text-muted-foreground text-xs">
                        {formatDate(log.created_at)}
                      </td>
                      <td className="p-4 text-xs text-red-400 max-w-xs truncate">
                        {log.error || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trigger Email Dialog */}
      <Dialog open={triggerDialog} onOpenChange={setTriggerDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email</DialogTitle>
            <DialogDescription>
              Manually trigger an email for a specific booking
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Booking Reference</label>
              <Input
                placeholder="e.g., VIP-XXXXXX"
                value={triggerBookingRef}
                onChange={(e) => setTriggerBookingRef(e.target.value.toUpperCase())}
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Email Type</label>
              <Select value={triggerEmailType} onValueChange={setTriggerEmailType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmation">Booking Confirmation</SelectItem>
                  <SelectItem value="payment_received">Payment Received</SelectItem>
                  <SelectItem value="reminder">24h Reminder</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setTriggerDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleTriggerEmail}
              disabled={triggering}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {triggering ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Send Email'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </div>
      </main>
    </div>
  );
}
